import React from 'react';
import { Check, Sparkles, TrendingUp, Building2, HelpCircle, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';

const Pricing = () => {
  const { language } = useLanguage();
  const t = useTranslation(language);
  const packages = [
  {
    tag: 'Entry',
    name: 'PE Foundations',
    price: 'C$5,500',
    period: '/month',
    duration: '3-month engagement',
    target: 'New PE portfolio companies needing quick financial infrastructure',
    icon: Sparkles,
    featured: false,
    deliverables: [
    'Financial diagnostic & gap analysis',
    'Chart of accounts cleanup & standardization',
    'Monthly financial close process design',
    'Basic KPI dashboard (10-15 metrics)',
    'Budget vs actual reporting template',
    'Monthly sponsor reporting package',
    '2 training sessions for finance team'],

    cta: 'Schedule a Call',
    ctaLink: '/contact'
  },
  {
    tag: 'Growth',
    name: 'Finance Transformation',
    tagline: 'Most Popular',
    price: 'C$9,500',
    period: '/month',
    duration: '3-6 month engagement',
    target: 'Portfolio companies scaling financial operations for growth',
    icon: TrendingUp,
    featured: true,
    deliverables: [
    'Everything in PE Foundations',
    'Full FP&A process build (budget, forecast, variance)',
    '3-statement financial model',
    'EBITDA bridge & waterfall reporting',
    'Rolling 13-week cash forecast',
    'Board deck design & monthly prep',
    'Weekly working sessions with finance team',
    'Value creation plan support'],

    cta: 'Schedule a Call',
    ctaLink: '/contact'
  },
  {
    tag: 'Strategic',
    name: 'PMI & Strategic Finance Partner',
    price: 'Custom',
    period: 'pricing',
    duration: '3-6 month engagement',
    target: 'Post-merger integration or complex multi-entity reporting',
    icon: Building2,
    featured: false,
    deliverables: [
    'Everything in Finance Transformation',
    'Multi-entity consolidation & integration',
    'Post-close 90-day finance roadmap',
    'Investor-grade reporting infrastructure',
    'Synergy tracking & value capture reporting',
    'CFO-level advisory & strategic support',
    'Fractional finance leadership',
    'Exit readiness planning'],

    cta: 'Request a Proposal',
    ctaLink: '/contact'
  }];


  const addons = [
  {
    name: 'Fractional CFO Services',
    description: 'Ongoing monthly CFO advisory, board deck prep, and strategic finance leadership',
    price: "C$3,500 / month"
  },
  {
    name: 'Post-Merger Integration Sprint',
    description: '90-day intensive program for financial system integration and reporting build post-acquisition',
    price: "C$25,000 fixed"
  },
  {
    name: 'Financial Model Build or Audit',
    description: 'Custom 3-statement model build or comprehensive audit of existing models',
    price: "Starting at C$2,500"
  }];


  const faqs = [
  {
    question: 'How does an engagement typically start?',
    answer: 'It starts with a free 30-minute consultation where we assess your current financial setup, identify the biggest gaps, and recommend the right pack. No pitch — just an honest conversation.'
  },
  {
    question: 'Can I extend beyond 3 months?',
    answer: 'Absolutely. Many clients continue with a monthly fractional CFO retainer or move into a follow-on project after the initial engagement.'
  },
  {
    question: 'Do you work with companies outside Quebec?',
    answer: 'Yes. We work with clients across Canada and internationally. All engagements can be delivered remotely, with on-site options available for Montreal-area clients.'
  },
  {
    question: 'What does AI integration training actually cover?',
    answer: 'We train your finance team on practical AI tools — how to use ChatGPT for analysis, Microsoft Copilot in Excel, AI-assisted forecasting, and automating repetitive reporting tasks. It\'s hands-on and specific to your workflows.'
  },
  {
    question: 'Is the pricing fixed or negotiable?',
    answer: 'The Starter and Growth packs are fixed-scope for predictable delivery. The PE/Enterprise pack is always custom-scoped based on complexity and entity count.'
  }];


  return (
    <div className="min-h-screen bg-white pt-20">
      <Helmet>
        <title>{t.meta.pricing.title}</title>
        <meta name="description" content={t.meta.pricing.description} />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0a1628] to-[#0f1f38] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              PE-Grade Finance Packages
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Fixed-scope engagements designed for PE sponsors and portfolio companies who need institutional-quality financial infrastructure - fast.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => {
              const Icon = pkg.icon;
              return (
                <div
                  key={index}
                  className={`relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden ${
                  pkg.featured ? 'border-4 border-[#2ecc8a] lg:-translate-y-4' : 'border border-gray-200'}`
                  }>

                  {/* Featured Badge */}
                  {pkg.featured &&
                  <div className="absolute top-0 right-0 bg-[#2ecc8a] text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                      {pkg.tagline}
                    </div>
                  }

                  <div className="p-8">
                    {/* Tag */}
                    <div className="flex items-center space-x-2 mb-4">
                      <Icon className="text-[#2ecc8a]" size={24} />
                      <span className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                        {pkg.tag}
                      </span>
                    </div>

                    {/* Package Name */}
                    <h3 className="text-2xl font-bold text-[#0a1628] mb-2">
                      {pkg.name}
                    </h3>

                    {/* Price */}
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-[#0a1628]">
                        {pkg.price}
                      </span>
                      <span className="text-gray-600">{pkg.period}</span>
                    </div>

                    {/* Duration & Target */}
                    <p className="text-sm text-gray-600 mb-2">
                      {pkg.duration}
                    </p>
                    <p className="text-sm text-gray-700 font-medium mb-6">{pkg.target}</p>

                    {/* Divider */}
                    <div className="border-t border-gray-200 mb-6"></div>

                    {/* Deliverables */}
                    <div className="space-y-3 mb-8">
                      {pkg.deliverables.map((item, idx) =>
                      <div key={idx} className="flex items-start space-x-3">
                          <Check className="text-[#2ecc8a] flex-shrink-0 mt-0.5" size={18} />
                          <span className="text-sm text-gray-700 leading-relaxed">{item}</span>
                        </div>
                      )}
                    </div>

                    {/* CTA Button */}
                    <Link
                      to={pkg.ctaLink}
                      className={`block w-full text-center px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                      pkg.featured ?
                      'bg-[#2ecc8a] text-white hover:bg-[#26b377] shadow-md hover:shadow-lg' :
                      'bg-[#0a1628] text-white hover:bg-[#0f1f38]'}`
                      }>

                      {pkg.cta}
                    </Link>
                  </div>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* Add-ons Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0a1628] mb-4">
              Add-on modules
            </h2>
            <p className="text-xl text-gray-600">
              Complement any pack with targeted expertise
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {addons.map((addon, index) =>
            <div
              key={index}
              className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-[#2ecc8a] hover:shadow-lg transition-all duration-300">

                <h3 className="text-xl font-bold text-[#0a1628] mb-3">
                  {addon.name}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  {addon.description}
                </p>
                <div className="text-lg font-semibold text-[#2ecc8a]">
                  {addon.price}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0a1628] mb-4">
              Common questions
            </h2>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) =>
            <div
              key={index}
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300">

                <div className="flex items-start space-x-4">
                  <HelpCircle className="text-[#2ecc8a] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="text-lg font-bold text-[#0a1628] mb-2">
                      {faq.question}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-[#0a1628]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Not sure which package is right?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Schedule a 30-minute call and we'll assess where you are and recommend the right engagement.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-[#2ecc8a] text-white font-semibold rounded-lg hover:bg-[#26b377] transition-all duration-300 shadow-lg hover:shadow-xl">

            Schedule a Call
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>);

};

export default Pricing;